/* global YT, Vimeo */

/*
  Patched (STEELPORT) — changes vs. stock Symmetry video.js:
  1. FIX: IntersectionObserver rootMargin was passed to entries.forEach()
     (as thisArg, silently ignored) instead of the IO constructor. Moved to
     the correct argument, restoring "start playing 200px early" behavior.
  2. NEW: download/init gate in connectedCallback. If the component has
     data-disable-mobile="true" and viewport < 768px, OR the user prefers
     reduced motion, the <video> element is removed before it can fetch and
     no player API is loaded. The section's placeholder image remains
     visible (video-section--played is never added).
  3. FIX: handle autoplay promise rejection (NotAllowedError) quietly.
  4. HARDEN: guard window.debounce in case script order changes.
  5. NEW: <responsive-video> element, which picks a desktop or mobile
     <video-component> at load time from inert nested <template>s, so the
     variant that isn't used never fetches. See its docblock.
*/

/*
  Chooses between a desktop and a mobile video without downloading both.

  Each variant lives in a nested <template data-media="desktop|mobile">.
  Template content is inert — it is not rendered and its media is not
  fetched — even once the surrounding markup is in the document, so the
  losing variant costs nothing. This element clones the winner in and drops
  both templates.

  It deliberately does NOT define or touch <video-component>: that element is
  registered by whichever of video.js / video-new.js parses first, and the
  variant choice must not depend on that race.
*/
if (!customElements.get('responsive-video')) {
  class ResponsiveVideo extends HTMLElement {
    connectedCallback() {
      // deferred-media clones us into the DOM once, but guard re-entry
      // (section re-render in the theme editor, moves between parents).
      if (this.resolved) return;
      this.resolved = true;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.disable();
        return;
      }

      const template = this.querySelector(`template[data-media="${this.variant()}"]`);
      if (!template) {
        // Only the other breakpoint has a video configured.
        this.disable();
        return;
      }

      const content = template.content.firstElementChild.cloneNode(true);
      this.dropTemplates();
      this.appendChild(content);

      // The iframe embed needs the 16:9 letterbox wrapper; a <video> (which
      // object-fit: covers) must not have it. Which one we got is only known
      // now, so the wrapper class is applied here rather than in Liquid.
      this.closest('.js-video-frame')?.classList.toggle(
        'video-section__bg-iframe-video',
        content.classList.contains('has-iframe')
      );
    }

    /**
     * Resolves which variant to render. Evaluated once — a resize across the
     * breakpoint needs a reload, the same trade-off videoDisabled() makes.
     * @returns {string} 'mobile' or 'desktop'.
     */
    variant() {
      const breakpoint = parseFloat(this.dataset.mobileBreakpoint) || 767.98;
      const isMobile = window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
      return isMobile && this.querySelector('template[data-media="mobile"]') ? 'mobile' : 'desktop';
    }

    /**
     * Renders no video at all, leaving the section's placeholder image.
     */
    disable() {
      this.dropTemplates();
      this.closest('.video-section')?.classList.add('video-section--video-disabled');
    }

    dropTemplates() {
      this.querySelectorAll(':scope > template[data-media]').forEach((el) => el.remove());
    }
  }

  customElements.define('responsive-video', ResponsiveVideo);
}

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
    }

    connectedCallback() {
      if (this.videoDisabled()) {
        // Remove the media element before it fetches; keep the placeholder.
        const video = this.querySelector('video');
        if (video) {
          video.removeAttribute('autoplay');
          video.remove();
        }
        this.closest('.video-section')?.classList.add('video-section--video-disabled');
        return;
      }

      this.init();
    }

    /**
     * Determines whether video should be skipped entirely (no download,
     * no player API) for this visitor.
     * Note: evaluated once at instantiation. A viewport resize across the
     * 768px boundary requires a reload to take effect — acceptable for the
     * data-saving use case this targets.
     * @returns {boolean}
     */
    videoDisabled() {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const mobileDisabled = this.dataset.disableMobile === 'true'
        && window.matchMedia('(max-width: 767.98px)').matches;
      return reducedMotion || mobileDisabled;
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        this.closest('.video-section').addEventListener('click', this.play.bind(this));
      }
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.closest('.video-section').classList.add('video-section--played');
      } else {
        this.player.addEventListener('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.addObserver(this.player);

        // Mute the video if a click didn't trigger its load
        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
        }

        this.play();
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        const setWidth = () => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        };
        window.addEventListener(
          'resize',
          typeof window.debounce === 'function' ? window.debounce(setWidth, 200) : setWidth
        );
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else {
        // play() returns a promise for <video>; autoplay may be blocked
        // (low-power mode, data saver). Placeholder + tap-to-play cover it.
        const result = this.player.play();
        if (result && typeof result.catch === 'function') {
          result.catch(() => {});
        }
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        });
      }, { rootMargin: '0px 0px 200px 0px' });

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}